/**
 * Authentication for listing search requests.
 *
 * Listing search is moving from Elasticsearch itself (the `cloudsearch*` host, which is
 * open to anyone) to marketplace-api's `/api/search/` endpoint, which requires a signed-in
 * user and applies event visibility on the server. Which one a marketplace uses is decided
 * by its `searchRoot` (`MarketplaceConfiguration.SearchUrl`), so the switch is made per
 * marketplace, in data, with no redeploy — and switched back the same way.
 *
 * Only marketplace-api gets credentials. The old host needs none, and sending it a custom
 * header would trigger a CORS preflight it was never configured to answer.
 */
import { getConfig, refreshApiKey } from './helpers';
import { getWithExpiry } from './helpers/localStorageWithExpiration';

const MARKETPLACE_API_SEARCH_PATH = '/api/search/';

/**
 * True when `searchRoot` is marketplace-api's search endpoint rather than Elasticsearch.
 * Decided by path, not origin, so it holds in production (same-origin, relative or absolute)
 * and in development (where marketplace-api may be on another origin).
 *
 * @param {string} [searchRoot] defaults to the configured `searchRoot`
 * @returns {boolean}
 */
export const isMarketplaceApiSearchRoot = (searchRoot = getConfig('searchRoot')) => {
  if (!searchRoot) return false;

  try {
    return new URL(searchRoot, window.location.origin).pathname
      .toLowerCase()
      .startsWith(MARKETPLACE_API_SEARCH_PATH);
  } catch (err) {
    return false;
  }
};

/**
 * Headers that authenticate a search request to marketplace-api: the same legacy ticket
 * (`apiKey`) every other data call already uses, sent as `Authorization: Ticket …`.
 *
 * @param {{ forceRefresh?: boolean }} [options] `forceRefresh` skips the stored key, for
 *   retrying after the server rejected it
 * @returns {Promise<Object<string, string>|null>} null when there is no key and none can be
 *   fetched, i.e. the user is not signed in
 */
export const getSearchAuthHeaders = async ({ forceRefresh = false } = {}) => {
  const apiKey =
    (!forceRefresh && getWithExpiry('apiKey')) ||
    (await refreshApiKey(getWithExpiry('refreshToken')));

  return apiKey ? { Authorization: `Ticket ${apiKey}` } : null;
};
